/**
 * src/pages/AdminMetricsPage.tsx
 *
 * Admin-only metrics dashboard at /admin (ADMIN-01).
 *
 * Status machine: 'loading' | 'forbidden' | 'error' | 'ok'
 *   loading   → skeleton cards (same grid layout as loaded state — no layout shift)
 *   forbidden → not-authorized empty state (403 from server — expected for non-admins)
 *   error     → API error state with Retry button
 *   ok        → MetricsGrid with header title + RefreshControl
 *
 * Security note: the server requireAdmin (19-01) is the real gate. This page's 403
 * handling is UX only (T-19-06). The page MUST NOT throw on 403 (Pitfall 3).
 *
 * Route: /admin (inside RequireAuth in App.tsx). Not linked in any user nav (D-05).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Activity, RefreshCcw } from 'lucide-react';
import { MetricsGrid, SkeletonCards, ForbiddenState, ErrorState } from '@/features/admin';
import type { AdminMetrics } from '@/features/admin';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Status = 'loading' | 'forbidden' | 'error' | 'ok';

// ---------------------------------------------------------------------------
// AdminMetricsPage
// ---------------------------------------------------------------------------

const AdminMetricsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<Status>('loading');
  const [metrics, setMetrics] = useState<AdminMetrics | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  // Stable ref for the fetch function so it can be called from event handlers
  const fetchRef = useRef<() => void>(() => undefined);

  const fetchMetrics = useCallback(() => {
    setStatus('loading');
    fetch('/api/admin/metrics', { credentials: 'include' })
      .then((res) => {
        if (res.status === 401 || res.status === 403) {
          setStatus('forbidden');
          return null;
        }
        if (!res.ok) {
          setStatus('error');
          return null;
        }
        return res.json() as Promise<AdminMetrics>;
      })
      .then((data) => {
        if (data) {
          setMetrics(data);
          setLastUpdated(new Date());
          setStatus('ok');
        }
      })
      .catch((err: unknown) => {
        console.error('[AdminMetricsPage] fetch failed', err);
        setStatus('error');
      });
  }, []);

  // Keep fetchRef in sync with latest fetchMetrics identity
  useEffect(() => {
    fetchRef.current = fetchMetrics;
  });

  // Initial fetch on mount only
  useEffect(() => {
    fetchRef.current();
  }, []);

  const formattedTime = lastUpdated
    ? lastUpdated.toLocaleTimeString(i18n.language, {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

  const isLoading = status === 'loading';

  return (
    <div className="flex flex-col h-screen bg-slate-50 dark:bg-slate-950 font-sans text-slate-900 dark:text-slate-100 overflow-hidden transition-colors duration-300">
      {/* aria-live region: announces when metrics finish loading (IN-01) */}
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {status === 'ok' ? t('admin.loaded', 'Metrics loaded') : ''}
      </span>

      {/* Header */}
      <header className="h-16 border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md flex items-center justify-between px-4 md:px-8 shrink-0 z-40">
        <div className="flex items-center gap-3">
          <Activity size={20} className="text-blue-600 dark:text-blue-400" aria-hidden="true" />
          <h1 className="text-subhead font-bold text-slate-900 dark:text-slate-50">
            {t('admin.page_title', 'Admin — Metrics')}
          </h1>
        </div>

        {/* RefreshControl */}
        {status !== 'forbidden' && (
          <div className="flex items-center gap-3">
            {lastUpdated && (
              <span className="text-caption text-slate-400 hidden sm:inline">
                {t('admin.refresh.last_updated', 'Updated')} {formattedTime}
              </span>
            )}
            <button
              aria-label={t('admin.refresh.aria', 'Refresh metrics')}
              aria-busy={isLoading}
              onClick={() => void fetchMetrics()}
              disabled={isLoading}
              className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <RefreshCcw size={16} className={isLoading ? 'animate-spin' : ''} />
            </button>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto px-4 md:px-10 pt-10 pb-10">
        <div className="max-w-[1400px] mx-auto">
          {/* Loading state */}
          {status === 'loading' && <SkeletonCards />}

          {/* Forbidden / not authorized (403) */}
          {status === 'forbidden' && <ForbiddenState />}

          {/* API error state */}
          {status === 'error' && <ErrorState onRetry={() => void fetchMetrics()} />}

          {/* Loaded — metrics grid */}
          {status === 'ok' && metrics && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
            >
              <MetricsGrid metrics={metrics} />
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminMetricsPage;
